import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Branch } from "@/types";

export interface InventoryLine {
  id: string;
  product_id: string;
  branch_id: string;
  stock: number;
  min_stock: number;
  unit: string | null;
  product_name: string;
  product_sku: string | null;
  product_image: string | null;
  cost_price: number | null;
  branch_name: string;
  low: boolean;
  out: boolean;
}

export interface MovementLine {
  id: string;
  product_id: string | null;
  branch_id: string | null;
  movement_type: string;
  quantity: number;
  reference: string | null;
  reason: string | null;
  notes: string | null;
  user_id: string | null;
  created_at: string;
  product_name: string;
  branch_name: string;
  user_name: string;
}

export interface ProductOption {
  id: string;
  name: string;
  sku: string | null;
  unit: string | null;
}

interface InventoryData {
  lines: InventoryLine[];
  movements: MovementLine[];
  products: ProductOption[];
  branches: Branch[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useInventoryData(movementLimit = 100): InventoryData {
  const [lines, setLines] = useState<InventoryLine[]>([]);
  const [movements, setMovements] = useState<MovementLine[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inventoryRes, productsRes, branchesRes, movementsRes, profilesRes] = await Promise.all([
        supabase.from("inventory").select("id, product_id, branch_id, stock, min_stock, unit"),
        supabase
          .from("products")
          .select("id, name, sku, unit, image_url, cost_price, price")
          .order("name"),
        supabase.from("branches").select("id, name, code, active").order("name"),
        supabase
          .from("inventory_movements")
          .select("id, product_id, branch_id, movement_type, quantity, reference, reason, notes, user_id, created_at")
          .order("created_at", { ascending: false })
          .limit(movementLimit),
        supabase.from("profiles").select("id, full_name, last_name"),
      ]);

      if (inventoryRes.error || productsRes.error) {
        setError("No se pudo cargar el inventario.");
        setLines([]);
        return;
      }

      const productList = (productsRes.data ?? []) as {
        id: string;
        name: string;
        sku: string | null;
        unit: string | null;
        image_url: string | null;
        cost_price: number | null;
        price: number | null;
      }[];
      const branchList = (branchesRes.data ?? []) as Branch[];

      const productMap = new Map(productList.map((p) => [p.id, p]));
      const branchMap = new Map(branchList.map((b) => [b.id, b.name]));
      const userMap = new Map(
        ((profilesRes.data ?? []) as { id: string; full_name: string; last_name: string }[]).map((u) => [
          u.id,
          `${u.full_name} ${u.last_name}`.trim(),
        ]),
      );

      const inventoryLines: InventoryLine[] = (inventoryRes.data ?? [])
        .map((row) => {
          const product = row.product_id ? productMap.get(row.product_id) : undefined;
          if (!row.product_id || !product) return null;
          const stock = Number(row.stock ?? 0);
          const minStock = Number(row.min_stock ?? 0);
          return {
            id: row.id,
            product_id: row.product_id,
            branch_id: row.branch_id ?? "",
            stock,
            min_stock: minStock,
            unit: row.unit ?? product.unit ?? null,
            product_name: product.name,
            product_sku: product.sku,
            product_image: product.image_url,
            cost_price: product.cost_price,
            branch_name: row.branch_id ? branchMap.get(row.branch_id) ?? "—" : "—",
            low: stock <= minStock && minStock > 0,
            out: stock <= 0,
          } as InventoryLine;
        })
        .filter((line): line is InventoryLine => line !== null);

      const movementLines: MovementLine[] = (movementsRes.data ?? []).map((row) => ({
        id: row.id,
        product_id: row.product_id,
        branch_id: row.branch_id,
        movement_type: row.movement_type,
        quantity: Number(row.quantity ?? 0),
        reference: row.reference,
        reason: row.reason,
        notes: row.notes,
        user_id: row.user_id,
        created_at: row.created_at,
        product_name: row.product_id ? productMap.get(row.product_id)?.name ?? "Producto" : "—",
        branch_name: row.branch_id ? branchMap.get(row.branch_id) ?? "—" : "—",
        user_name: row.user_id ? userMap.get(row.user_id) ?? "—" : "—",
      }));

      setLines(inventoryLines);
      setMovements(movementLines);
      setProducts(
        productList.map((p) => ({ id: p.id, name: p.name, sku: p.sku, unit: p.unit })),
      );
      setBranches(branchList);
    } catch {
      setError("No se pudo cargar el inventario.");
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [movementLimit]);

  useEffect(() => {
    load();
  }, [load]);

  return { lines, movements, products, branches, loading, error, reload: load };
}